import { sleep } from "@/lib/utils";

import type { Api, AuditEntry, AuditFacets, AuditPage } from "../types";

// Офлайн-журнал действий консоли: несколько записей разных разделов, чтобы
// страница «Журнал» жила без бэкенда и было видно, как выглядят и успешное
// действие, и отказ доступа. Форма повторяет DTO шлюза (services/auditLog.ts).
const MINUTE = 60_000;
const at = (minutesAgo: number) => new Date(Date.now() - minutesAgo * MINUTE).toISOString();

const entries: AuditEntry[] = [
	{
		id: "aal_demo_1",
		actorUserId: "usr_demo_admin",
		actorName: "Платформа",
		actorEmail: "admin@osmix.ru",
		entityKind: "products",
		entityId: "prd_demo_ring",
		action: "reject",
		reason: "Фото не соответствует карточке",
		metadata: { body: { reason: "Фото не соответствует карточке", reasonCode: "photo" } },
		method: "POST",
		path: "/api/v1/admin/products/prd_demo_ring/reject",
		route: "/api/v1/admin/products/:id/reject",
		statusCode: 200,
		durationMs: 42,
		createdAt: at(4),
	},
	{
		id: "aal_demo_2",
		actorUserId: "usr_demo_manager",
		actorName: "Менеджер площадки",
		entityKind: "shops",
		entityId: "shop-uyut",
		action: "block",
		reason: "Жалобы покупателей на сроки",
		metadata: { body: { reason: "Жалобы покупателей на сроки" } },
		method: "POST",
		path: "/api/v1/admin/shops/shop-uyut/block",
		route: "/api/v1/admin/shops/:id/block",
		statusCode: 200,
		durationMs: 31,
		createdAt: at(38),
	},
	{
		id: "aal_demo_3",
		actorUserId: "usr_demo_manager",
		actorName: "Менеджер площадки",
		entityKind: "sellers",
		entityId: "usr_demo_seller",
		action: "documents.file",
		metadata: { read: true, params: { userId: "usr_demo_seller", docId: "doc_demo_inn" } },
		method: "GET",
		path: "/api/v1/admin/sellers/usr_demo_seller/documents/doc_demo_inn/file",
		route: "/api/v1/admin/sellers/:userId/documents/:docId/file",
		statusCode: 200,
		durationMs: 88,
		createdAt: at(95),
	},
	{
		id: "aal_demo_4",
		actorName: "",
		entityKind: "finances",
		entityId: "ob_demo_1",
		action: "obligations.advance",
		metadata: { params: { id: "ob_demo_1" } },
		method: "POST",
		path: "/api/v1/admin/finances/obligations/ob_demo_1/advance",
		route: "/api/v1/admin/finances/obligations/:id/advance",
		// Отказ доступа: запись остаётся без автора, но остаётся — по ней видно
		// попытку зайти в чужой раздел.
		statusCode: 403,
		durationMs: 6,
		createdAt: at(140),
	},
];

const matches = (entry: AuditEntry, filter: Parameters<Api["audit"]["list"]>[0] = {}): boolean => {
	if (filter.actorUserId && entry.actorUserId !== filter.actorUserId) return false;
	if (filter.entityKind && entry.entityKind !== filter.entityKind) return false;
	if (filter.entityId && entry.entityId !== filter.entityId) return false;
	if (filter.action && entry.action !== filter.action) return false;
	if (filter.q) {
		const needle = filter.q.toLowerCase();
		const hay = [entry.path, entry.reason, entry.entityId, entry.actorName]
			.filter(Boolean)
			.join(" ")
			.toLowerCase();
		if (!hay.includes(needle)) return false;
	}
	return true;
};

export const audit: Api["audit"] = {
	list: async (filter = {}): Promise<AuditPage> => {
		await sleep(120);
		const found = entries.filter((entry) => matches(entry, filter));
		const page = filter.page ?? 1;
		const pageSize = filter.pageSize ?? 50;
		return {
			items: found.slice((page - 1) * pageSize, page * pageSize),
			total: found.length,
			page,
			pageSize,
		};
	},

	facets: async (): Promise<AuditFacets> => {
		await sleep(80);
		const count = <T extends string>(values: T[]) => {
			const map = new Map<T, number>();
			for (const value of values) map.set(value, (map.get(value) ?? 0) + 1);
			return [...map.entries()].map(([value, c]) => ({ value, count: c }));
		};
		return {
			kinds: count(entries.map((e) => e.entityKind)),
			actions: count(entries.map((e) => e.action)),
			actors: [...new Map(entries.filter((e) => e.actorUserId).map((e) => [e.actorUserId ?? "", e])).values()].map(
				(e) => ({
					id: e.actorUserId ?? "",
					name: e.actorName,
					count: entries.filter((x) => x.actorUserId === e.actorUserId).length,
				}),
			),
		};
	},

	forEntity: async (kind, id): Promise<AuditEntry[]> => {
		await sleep(80);
		return entries.filter((entry) => entry.entityKind === kind && entry.entityId === id);
	},
};
